import { AnimatePresence, motion } from 'framer-motion'
import { forwardRef, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import Icon from '@/components/Icon'

import useKeyboardDialogOffset from '../Keyboard/useKeyboardDialogOffset'
import { NumericKeyboardButton, NumericKeyboardPortal, NumericKeyboardWrapper } from './NumericKeyboard.styles'

interface NumericKeyboardProps {
  value?: string | null
  onChange: (value: string) => void
  maxLength?: number
  visible?: boolean
  offsetDialog?: boolean
  animateOnMount?: boolean
  onHeightChange?: (height: number) => void
}

const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'backspace']

const NumericKeyboard = forwardRef<HTMLDivElement, NumericKeyboardProps>(({ value, onChange, maxLength = 10, visible = true, offsetDialog = true, animateOnMount = true, onHeightChange }, ref) => {
  const keyboardRef = useRef<HTMLDivElement | null>(null)
  useKeyboardDialogOffset(offsetDialog && visible, keyboardRef)

  useLayoutEffect(() => {
    if (!visible || !keyboardRef.current || !onHeightChange) {
      return
    }

    onHeightChange(keyboardRef.current.getBoundingClientRect().height)
  }, [onHeightChange, visible])

  const handleKeyPress = (key: string) => {
    if (key === 'backspace') {
      onChange((value ?? '').slice(0, -1))
    } else if (!value || value.length < maxLength) {
      onChange((value ?? '') + key)
    }
  }

  const keyboardRoot = document.querySelector('#modal') ?? document.body

  return createPortal(
    <AnimatePresence>
      {visible && (
        <NumericKeyboardPortal
          ref={keyboardRef}
          data-element="keyboard-portal"
          as={motion.div}
          onPointerDown={event => event.preventDefault()}
          onContextMenu={event => event.preventDefault()}
          initial={animateOnMount ? { y: '100%', opacity: 0 } : false}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <NumericKeyboardWrapper ref={ref}>
            {keys.map(key => (
              <NumericKeyboardButton key={key} type="button" onPointerDown={event => event.preventDefault()} onClick={() => handleKeyPress(key)} wide={key === 'backspace'}>
                {key === 'backspace' ? <Icon name="key-backspace" /> : key}
              </NumericKeyboardButton>
            ))}
          </NumericKeyboardWrapper>
        </NumericKeyboardPortal>
      )}
    </AnimatePresence>,
    keyboardRoot
  )
})

NumericKeyboard.displayName = 'NumericKeyboard'

export default NumericKeyboard
